import { Badge, Box, CircularProgress, Fab, SvgIcon } from '@mui/material'
import { PropTypes } from 'prop-types'
import { useMemo, useRef } from 'react'
import { useEffect, useState } from 'react'
import Draggable from 'react-draggable'

import BarcodeIcon from '@hapana/shared/web/styles/assets/svgs/barcode.svg'

// TODO: useLatestCheckIn should be passed as props to avoid circular dependency
// import { useLatestCheckIn } from "../../../../web/check-in/src/app.hooks";

import {
  FLOAT_BUTTON_BADGE_CONFIG,
  FLOAT_BUTTON_BOUNDS,
  FLOAT_BUTTON_CONFIG,
  FLOAT_BUTTON_STORAGE_CONFIG,
  FLOAT_BUTTON_BADGE_COUNTER_MAX,
  FLOAT_BUTTON_DRAG_PRECISION,
  NEW_CHECKIN_STATUSES,
} from './float-button.constants'
import { styles } from './float-button.styles'

const loadPosition = () => {
  return (
    JSON.parse(localStorage.getItem(FLOAT_BUTTON_STORAGE_CONFIG.checkInPosition)) || {
      x: 0,
      y: 0,
    }
  )
}

const FloatButton = ({ onClick, counter, sx, isLoading, newCheckIn, isVisible }) => {
  const [position, setPosition] = useState(loadPosition)
  const [isDragging, setIsDragging] = useState(false)

  const [animationStyles, setAnimationStyles] = useState({})
  const [badgeAnimationStyles, setBadgeAnimationStyles] = useState({})
  const buttonRef = useRef(null)
  // TODO: This should be passed as props to avoid circular dependency
  const latestCheckIn = {} // const { value: latestCheckIn } = useLatestCheckIn();

  useEffect(() => {
    if (counter) {
      setAnimationStyles(styles.animation)
      setBadgeAnimationStyles(styles.badgeAnimation)
    }
  }, [counter])

  const handleStop = (_, positionData) => {
    if (isDragging) {
      const { x, y } = positionData
      localStorage.setItem(FLOAT_BUTTON_STORAGE_CONFIG.checkInPosition, JSON.stringify({ x, y }))
    } else {
      onClick()
    }
    setIsDragging(false)
  }

  const newCheckInStatus = useMemo(() => {
    const currentCheckinData = newCheckIn.status ? newCheckIn : latestCheckIn
    if (counter && currentCheckinData) {
      if (currentCheckinData.status?.toUpperCase() === NEW_CHECKIN_STATUSES.SUCCESS.toUpperCase()) {
        if (currentCheckinData.hasOutstandingOrFailedPayments !== 'true') {
          return NEW_CHECKIN_STATUSES.VALID_CHECK_IN
        }
        return NEW_CHECKIN_STATUSES.VALID_CHECK_IN_WITH_ALERTS
      } else if (
        currentCheckinData.status?.toUpperCase() === NEW_CHECKIN_STATUSES.FAIL.toUpperCase()
      ) {
        return NEW_CHECKIN_STATUSES.INVALID_CHECK_IN
      }
    }
    return NEW_CHECKIN_STATUSES.DEFAULT_NO_CHECKIN
  }, [counter, newCheckIn, latestCheckIn])

  const handleDrag = (_, positionData) => {
    const deltaX = positionData.x - position.x
    const deltaY = positionData.y - position.y

    if (
      Math.abs(deltaX) > FLOAT_BUTTON_DRAG_PRECISION ||
      Math.abs(deltaY) > FLOAT_BUTTON_DRAG_PRECISION
    ) {
      setIsDragging(true)
      const { x, y } = positionData
      setPosition({ x, y })
    }
  }

  return (
    <>
      <Draggable
        bounds={FLOAT_BUTTON_BOUNDS}
        onStop={handleStop}
        onDrag={handleDrag}
        position={position}
        nodeRef={buttonRef}
      >
        <Box ref={buttonRef} sx={[styles.position, !isVisible && styles.hideFloatButton]}>
          <Fab
            onAnimationEnd={() => {
              setAnimationStyles(styles.resetAnimation)
              setBadgeAnimationStyles(styles.resetAnimation)
            }}
            sx={[
              styles.floatButton,
              styles.color,
              styles.size,
              animationStyles,
              styles[newCheckInStatus],
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...FLOAT_BUTTON_CONFIG}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {counter > 0 && (
                  <Badge
                    sx={[styles.badge, badgeAnimationStyles, styles[newCheckInStatus]]}
                    badgeContent={
                      counter > FLOAT_BUTTON_BADGE_COUNTER_MAX
                        ? `${FLOAT_BUTTON_BADGE_COUNTER_MAX}+`
                        : counter
                    }
                    {...FLOAT_BUTTON_BADGE_CONFIG}
                  ></Badge>
                )}
                <SvgIcon component={BarcodeIcon} fontSize="inherit" />
              </>
            )}
          </Fab>
        </Box>
      </Draggable>
    </>
  )
}

FloatButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  counter: PropTypes.number,
  sx: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.array]),
  isLoading: PropTypes.bool,
  newCheckIn: PropTypes.shape({
    status: PropTypes.string,
    hasOutstandingOrFailedPayments: PropTypes.string,
    checkInId: PropTypes.string,
    timestamp: PropTypes.string,
  }),
  isVisible: PropTypes.bool,
}

FloatButton.defaultProps = {
  counter: 0,
  sx: {},
  newCheckIn: {},
  isLoading: false,
  isVisible: true,
}

export default FloatButton
